import { useEffect, useMemo, useState } from "react";
import { Upload, CheckCircle, AlertTriangle, X, Clock, RotateCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { LastUpdateStat } from "./XlsxUploadCard";

const PHASE_LABELS = {
  queued: "En cola…",
  uploading: "Subiendo archivo…",
  starting: "Iniciando…",
  parsing: "Leyendo archivo (CSV/XLSX)…",
  upserting: "Procesando leads…",
  archiving: "Archivando snapshot…",
  pruning: "Limpiando datos obsoletos…",
  done: "Listo",
};
function phaseLabel(phase) {
  return PHASE_LABELS[phase] || (phase ? `Fase: ${phase}` : "Procesando…");
}

/**
 * Inline CSV upload card — used inside the admin integrations panel.
 * Identical functionality to the standalone /datos page: async background jobs
 * with live progress bar + replace/append toggle + xlsx support.
 */
export default function CSVUploadCard({ onUploaded }) {
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // "replace-all" · "replace-period" · "append"
  const [mode, setMode] = useState("replace-all");
  const [replaceYear, setReplaceYear] = useState("");
  const [replaceMonth, setReplaceMonth] = useState("");
  const currentYear = new Date().getFullYear();
  const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  const [jobStatus, setJobStatus] = useState(null);
  // History for the "Última actualización" stat at the top of the card.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const refreshHistory = () => {
    setHistoryLoading(true);
    api.get("/upload-jobs", { params: { kind: "leads_raw", limit: 10 } })
      .then((r) => setHistory(r.data?.jobs || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };
  useEffect(() => { refreshHistory(); }, []);
  const lastSuccessful = useMemo(
    () => (history || []).find((j) => j.status === "done") || null,
    [history],
  );

  const pollJob = async (jobId, toastId) => {
    const startedAt = Date.now();
    let warnedSlow = false;
    while (true) {
      const elapsedMin = (Date.now() - startedAt) / 60000;
      try {
        const r = await api.get(`/upload-jobs/${jobId}`);
        const d = r.data;
        setJobStatus({
          phase: d.phase,
          current: d.progress_current || 0,
          total: d.progress_total || 0,
          elapsedMin: Math.floor(elapsedMin),
        });
        if (d.status === "done") {
          setResult(d.result || {});
          toast.success(`CSV cargado · ${(d.result?.total ?? 0).toLocaleString()} registros`, {
            id: toastId,
            description: "El dashboard CRM Overview ya refleja los datos.",
          });
          if (onUploaded) onUploaded(d.result || {});
          refreshHistory();
          return;
        }
        if (d.status === "error") {
          const msg = d.error || "Error en el procesamiento";
          setError(msg);
          toast.error("Error al procesar CSV", { id: toastId, description: msg });
          return;
        }
      } catch (e) { /* transient — keep polling */ }

      // Nudge the user if the job has been running > 2 min: raises awareness
      // without cancelling. Prod backend has an internal 10-min cleanup.
      if (!warnedSlow && elapsedMin >= 2) {
        warnedSlow = true;
        toast.loading(
          `Procesando… (${Math.floor(elapsedMin)} min). Archivos grandes pueden demorar.`,
          { id: toastId, description: "El servidor limpia jobs colgados a los 10 min." }
        );
      }
      // Hard stop at 10 min — matches the backend cleanup window so we don't
      // spin forever after the server has already marked the job as error.
      if (elapsedMin >= 10) {
        const msg = "Tiempo excedido (10 min). El archivo puede ser demasiado grande o el servidor tuvo un problema. Intenta con un archivo más pequeño o divídelo en partes.";
        setError(msg);
        toast.error("Upload cancelado", { id: toastId, description: msg });
        return;
      }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const upload = async () => {
    if (!csvFile) {
      setError("Selecciona un archivo CSV o XLSX");
      toast.warning("Selecciona un archivo primero");
      return;
    }
    if (mode === "replace-period" && (!replaceYear || !replaceMonth)) {
      setError("Selecciona Año y Mes para reemplazar un período");
      toast.warning("Selecciona Año y Mes");
      return;
    }
    setError("");
    setResult(null);
    setJobStatus({ phase: "uploading", current: 0, total: 0 });
    setUploading(true);
    const fileName = csvFile.name;
    const toastId = toast.loading(`Subiendo ${fileName}…`);
    const fd = new FormData();
    fd.append("file", csvFile);
    const replaceParam = mode !== "append";
    const params = new URLSearchParams({ replace: String(replaceParam) });
    if (mode === "replace-period") {
      params.append("replace_year", replaceYear);
      params.append("replace_month", replaceMonth);
    }
    try {
      const r = await api.post(`/leads/upload-csv?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (r.data?.job_id) {
        toast.loading(`Procesando ${fileName} en segundo plano…`, { id: toastId });
        await pollJob(r.data.job_id, toastId);
      } else {
        // Legacy synchronous fallback
        setResult(r.data);
        toast.success(`CSV cargado · ${r.data.total ?? 0} registros`, {
          id: toastId,
          description: "El dashboard CRM Overview ya refleja los datos.",
        });
        if (onUploaded) onUploaded(r.data);
      }
      setCsvFile(null);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Error en la carga";
      setError(msg);
      toast.error("Error al subir CSV", { id: toastId, description: msg });
    } finally {
      setUploading(false);
      setJobStatus(null);
    }
  };

  return (
    <div data-testid="csv-upload-inline">
      <LastUpdateStat job={lastSuccessful} loading={historyLoading} testid="csv-upload-inline" maxDays={7}/>
      <div
        className={"dropzone" + (csvFile ? " drag" : "")}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setCsvFile(f); }}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => document.getElementById("csv-input-inline").click()}
        data-testid="csv-dropzone-inline"
      >
        <Upload size={32} style={{ margin: "0 auto 8px", display: "block", color: "#6b7280" }}/>
        <div className="font-bold text-base flex items-center justify-center gap-2">
          <span>{csvFile ? csvFile.name : "Arrastra tu CSV/XLSX o haz clic para seleccionar"}</span>
          {csvFile && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setCsvFile(null);
                const input = document.getElementById("csv-input-inline");
                if (input) input.value = "";
              }}
              className="rounded-full p-1 hover:bg-red-100 transition"
              style={{ color: "#dc2626", lineHeight: 0 }}
              title="Quitar archivo"
              data-testid="csv-remove-file"
            ><X size={14}/></button>
          )}
        </div>
        <div className="muted-small mt-2 text-xs">
          CSV (separador <code>;</code>, latin-1 o UTF-8) o XLSX (Excel) · ≤ 500K filas
        </div>
        <input id="csv-input-inline" type="file" accept=".csv,.xlsx" hidden onChange={(e) => setCsvFile(e.target.files?.[0])}/>
      </div>

      <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
        <div className="text-xs text-gray-500 font-semibold">
          Columnas mínimas: <code>(No modificar) Cliente potencial</code> · <code>Estado</code> · <code>Marca de Interés</code>
        </div>
        <button className="btn primary flex items-center gap-2" onClick={upload} disabled={!csvFile || uploading} data-testid="csv-upload-btn">
          {uploading ? <span className="spinner"/> : <Upload size={14}/>}
          {uploading
            ? "Procesando…"
            : mode === "append"
              ? "Subir y añadir"
              : mode === "replace-period"
                ? "Subir y reemplazar período"
                : "Subir y reemplazar"}
        </button>
      </div>

      {/* Replace-all vs Replace-period vs Append toggle */}
      <div
        className="mt-3 p-3 rounded-xl border"
        style={{ borderColor: "#e5e7eb", background: "#fafbfc" }}
        data-testid="upload-mode-card"
      >
        <div className="font-bold mb-2" style={{ fontSize: 12, color: "#374151" }}>Modo de carga</div>
        <div className="flex flex-col gap-2">
          <label className="flex items-start gap-2 cursor-pointer text-xs" data-testid="upload-mode-replace">
            <input
              type="radio"
              name="upload-mode-inline"
              checked={mode === "replace-all"}
              onChange={() => setMode("replace-all")}
              style={{ marginTop: 3 }}
            />
            <div>
              <div className="font-semibold" style={{ fontSize: 12 }}>Reemplazar todos los datos</div>
              <div className="muted-small">
                El CSV/XLSX es la <strong>fuente completa</strong>. Leads que NO estén en el archivo se eliminan.
                Los datos no se borran hasta que termine OK (a prueba de timeouts).
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-xs" data-testid="upload-mode-replace-period">
            <input
              type="radio"
              name="upload-mode-inline"
              checked={mode === "replace-period"}
              onChange={() => setMode("replace-period")}
              style={{ marginTop: 3 }}
            />
            <div className="flex-1">
              <div className="font-semibold" style={{ fontSize: 12 }}>Reemplazar solo un Año + Mes específico</div>
              <div className="muted-small">
                Igual que Oportunidades/Cotizaciones. Elimina solo los leads dentro de ese período
                que no estén en el archivo. <strong>Otros meses no se tocan.</strong>
              </div>
              {mode === "replace-period" && (
                <div className="flex gap-2 mt-2">
                  <select value={replaceYear} onChange={(e) => setReplaceYear(e.target.value)}
                          className="text-input text-xs" style={{ maxWidth: 110 }}
                          data-testid="csv-replace-year">
                    <option value="">Año…</option>
                    {[currentYear, currentYear - 1, currentYear - 2].map((y) =>
                      <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={replaceMonth} onChange={(e) => setReplaceMonth(e.target.value)}
                          className="text-input text-xs" style={{ maxWidth: 110 }}
                          data-testid="csv-replace-month">
                    <option value="">Mes…</option>
                    {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-xs" data-testid="upload-mode-append">
            <input
              type="radio"
              name="upload-mode-inline"
              checked={mode === "append"}
              onChange={() => setMode("append")}
              style={{ marginTop: 3 }}
            />
            <div>
              <div className="font-semibold" style={{ fontSize: 12 }}>Añadir / Actualizar (append)</div>
              <div className="muted-small">
                Inserta nuevos y actualiza existentes por <code>lead_id</code>. <strong>Nada se elimina.</strong>
              </div>
            </div>
          </label>
        </div>
      </div>

      {jobStatus && uploading && (
        <div className="mt-3 p-3 rounded-xl border" style={{ borderColor: "#dbeafe", background: "#eff6ff" }} data-testid="upload-progress-inline">
          <div className="flex justify-between items-center mb-2 text-xs font-bold" style={{ color: "#1e40af" }}>
            <span>{phaseLabel(jobStatus.phase)}</span>
            <span className="font-mono">
              {jobStatus.total > 0 ? `${jobStatus.current.toLocaleString()} / ${jobStatus.total.toLocaleString()}` : "…"}
            </span>
          </div>
          <div style={{ height: 6, background: "#dbeafe", borderRadius: 999, overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: jobStatus.total > 0 ? `${Math.min(100, (jobStatus.current / jobStatus.total) * 100)}%` : "12%",
              background: "linear-gradient(90deg, #3b82f6, #2563eb)",
              transition: "width 0.4s ease",
            }}/>
          </div>
          <div className="muted-small mt-2 text-xs" style={{ color: "#1e40af" }}>
            Puedes cerrar esta pantalla — el proceso continúa en segundo plano.
          </div>
        </div>
      )}
      {error && (
        <div className="mt-3 p-3 rounded-xl border border-red-200 bg-red-50 text-red-800 font-semibold text-sm flex items-start gap-2" data-testid="upload-error-inline">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5"/>
          <div>{error}</div>
        </div>
      )}
      {result && (
        <div className="mt-3 p-3 rounded-xl border border-green-200 bg-green-50 text-green-900 font-semibold text-sm flex items-start gap-2" data-testid="upload-success-inline">
          <CheckCircle size={16} className="flex-shrink-0 mt-0.5"/>
          <div>¡Carga exitosa! Procesados <strong>{(result.total ?? 0).toLocaleString()}</strong> registros. El dashboard CRM Overview ya refleja los datos.</div>
        </div>
      )}
    </div>
  );
}
